import { FC, ReactElement, useEffect, useState } from 'react';
import { TrendingUp, Download, FileText, Funnel, Clock, CircleAlert, Calendar } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { BackButton } from '../components/BackButton';
import { documentosApi, prestacoesApi, unidadesApi } from '../api/backend';
import { useAuthStore } from '../store/authStore';
import type { DocumentoFiscal, DocumentoFiscalStatus, DocumentosConsultaOrdem, TipoFiltroPeriodo, Unidade } from '../types';
import * as XLSX from 'xlsx';
import { GeneratedReportFile, ReportDataType, ReportFormatType } from '../constants/types';
import { downloadReportFile } from '../helpers/download-report-file';
import ExportButton from '../components/ExportButton';
import FilterToggleButton from '../components/FilterToggleButton';
import { toCSV } from '../helpers/toCSV';
import { normalizeReportData } from '../helpers/normalize-report-data';
import { formatCurrencyWithSymbol } from '../helpers/format-money';
import { add } from 'date-fns'
import { formatDate } from '../helpers/format-date';
import { getStatusConfig } from '../helpers/get-status-config';

const statusOptions: Array<{label: string, value: DocumentoFiscalStatus}> = [
  {
    label: "Pendentes",
    value: 'pendente'
  },
  {
    label: "Deferidos",
    value: 'deferido'
  },
  {
    label: "Indeferidos",
    value: 'indeferido'
  }
]

const tipoFiltroPeriodoOptions: Array<{label: string, value: TipoFiltroPeriodo}> = [
  {
    label: "Deferidos no período",
    value: 'deferido'
  },
  {
    label: "Lançados no período",
    value: 'lancamento'
  },
  {
    label: "Vencidos no período",
    value: 'vencimento'
  }
]

const applyReportFormat = (
  format: ReportFormatType,
  dataType: ReportDataType,
  data: any
): GeneratedReportFile | null => {
  const rows = normalizeReportData(data);

  if ((format === 'csv' || format === 'xlsx') && rows.length === 0) {
    alert('Nenhum dado encontrado para o período selecionado.');
    return null;
  }

  const baseFileName = `relatorio-${dataType}-${new Date().toISOString().slice(0, 10)}`;

  switch (format) {
    case 'pdf':
      alert('Formato PDF ainda não disponível');
      return null;

    case 'csv': {
      const content = toCSV(rows);
      return {
        content,
        mimeType: 'text/csv;charset=utf-8;',
        extension: 'csv',
        suggestedFileName: `${baseFileName}.csv`,
      };
    }

    case 'xlsx': {
      const wb = XLSX.utils.book_new();

      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws);

      if (wb.SheetNames.length === 0) {
        alert('Nenhum dado encontrado para o período selecionado.');
        return null;
      }

      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
      });

      return {
        content: wbout,
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extension: 'xlsx',
        suggestedFileName: `${baseFileName}.xlsx`,
      };
    }

    default:
      return null;
  }
}

type FastFilterButtonProps = {
  icon?: ReactElement
  title: string,
  selected?: boolean
  onClick?: () => void
}

const FastFilterButton: FC<FastFilterButtonProps> = ({ icon, title, selected, onClick }) => {
  const handleClick = () => {
    if (onClick) onClick()
  }

  const colorVariant = selected
    ? 'bg-primary-100 dark:bg-primary-700 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-600'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'

  return (
    <button onClick={handleClick} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${colorVariant}`}>
      {icon}
      <span>{title}</span>{}  
    </button>
  )
}

export function DocumentosPage() {
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [documentos, setDocumentos] = useState<DocumentoFiscal[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState<DocumentoFiscalStatus>(null);
  const [filtroTipoPeriodo, setFiltroTipoPeriodo] = useState<TipoFiltroPeriodo>(null);
  const [filtroDataInicio, setFiltroDataInicio] = useState<string>(null);
  const [filtroDataFim, setFiltroDataFim] = useState<string>(null);
  const [filtroUnidade, setFiltroUnidade] = useState<number | undefined>();
  const [ordem, setOrdem] = useState<DocumentosConsultaOrdem>('nome_asc')
  const { selectedUnidadeId } = useAuthStore();
  
  useEffect(() => {
    const loadUnidades = async () => {
      const unidadesData = await unidadesApi.list()
      setUnidades(unidadesData);
    }
    
    loadUnidades()
  }, [])

  useEffect(() => {
    // Se o usuário selecionou uma unidade específica, filtrar automaticamente
    if (selectedUnidadeId !== null && selectedUnidadeId !== undefined) {
      setFiltroUnidade(selectedUnidadeId);
    }
  }, [selectedUnidadeId]);

  useEffect(() => {
    loadData();
  }, [ordem, filtroStatus, filtroUnidade, filtroTipoPeriodo, filtroDataInicio, filtroDataFim]);

  const loadData = async () => {
    try {
      setLoading(true);

      const documentosData = await documentosApi.search({
        ...(filtroStatus ? { status: filtroStatus } : {}),
        ...(filtroUnidade ? { unidadeId: filtroUnidade } : {}),
        ...(filtroTipoPeriodo === 'lancamento' && filtroDataInicio ? { lancadoDepoisDe: filtroDataInicio } : {}),
        ...(filtroTipoPeriodo === 'lancamento' && filtroDataFim ? { lancadoAntesDe: filtroDataFim } : {}),
        ...(filtroTipoPeriodo === 'vencimento' && filtroDataInicio ? { vencidoDepoisDe: filtroDataInicio } : {}),
        ...(filtroTipoPeriodo === 'vencimento' && filtroDataFim ? { vencidoAntesDe: filtroDataFim } : {}),
        ...(filtroTipoPeriodo === 'deferido' && filtroDataInicio ? { deferidoDepoisDe: filtroDataInicio } : {}),
        ...(filtroTipoPeriodo === 'deferido' && filtroDataFim ? { deferidoAntesDe: filtroDataFim } : {}),
        ...(ordem ? { ordem } : {})
      })
    
      setDocumentos(documentosData)
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: ReportFormatType) => {
    //const data = await documentosApi.list(null, filtroUnidade);
    const file = applyReportFormat(format, 'documentos', documentos);

    if (!file) return;

    downloadReportFile(file);
  }

  const setFiltroRapido = (value: DocumentosConsultaOrdem) => {
    setOrdem(ordem === value ? 'nome_asc' : value)
  }

  const resetFiltros = () => {
    setFiltroStatus(null)
    setFiltroUnidade(null)
    setFiltroTipoPeriodo(null)
    setFiltroDataInicio(null)
    setFiltroDataFim(null)
    setOrdem('nome_asc')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <BackButton to="/dashboard" />
        </div>

        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Documentos Fiscais
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {documentos?.length || 0} documentos encontrados
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <FilterToggleButton
              active={filtros}
              onClick={() => setFiltros(!filtros)}
            />

            <ExportButton
              title='Exportar CSV'
              color='blue'
              onClick={() => exportReport('csv')}
            />

            <ExportButton
              title='Exportar Excel'
              color='green'
              onClick={() => exportReport('xlsx')}
            />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            <Funnel className="w-4 h-4 inline mr-2"/> <span>Filtros Rápidos</span>
          </label>
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3'>
            <FastFilterButton
              title='Últimos Incluídos'
              icon={<Clock className="w-4 h-4 inline mr-1"/>}
              selected={ordem === 'criacao_desc'}
              onClick={() => {
                setFiltroRapido('criacao_desc')
                setFiltroTipoPeriodo(null)
                setFiltroDataInicio(null)
                setFiltroDataFim(null)
              }}
            />
            <FastFilterButton
              title='Vencimentos Próximos'
              icon={<CircleAlert className="w-4 h-4 inline mr-1"/>}
              selected={ordem === 'vencimento_asc'}
              onClick={() => {
                setFiltroRapido('vencimento_asc')
                setFiltroTipoPeriodo('vencimento')
                setFiltroDataInicio(new Date().toISOString().split('T')[0])
                setFiltroDataFim(add(new Date(), { days: 14 }).toISOString().split('T')[0])
              }}
            />
            <FastFilterButton
              title='Limpar Filtros'
              onClick={() => resetFiltros()}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value as DocumentoFiscalStatus)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value={""}>Todos</option>
                {statusOptions.map(({ label, value }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Unidade
              </label>
              <select
                value={filtroUnidade || ''}
                onChange={(e) => setFiltroUnidade(e.target.value ? Number(e.target.value) : undefined)}
                disabled={selectedUnidadeId !== null && selectedUnidadeId !== undefined}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Todas as unidades</option>
                {unidades.map((unidade) => (
                  <option key={unidade.id} value={unidade.id}>
                    {unidade.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Filtro por Período
              </label>
              <select
                value={filtroTipoPeriodo || ''}
                onChange={(e) => setFiltroTipoPeriodo(e.target.value as TipoFiltroPeriodo)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Selecione um tipo de filtro</option>
                {tipoFiltroPeriodoOptions.map(({label, value}) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>              
            </div>
            
            {!!filtroTipoPeriodo && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data Início
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    value={filtroDataInicio}
                    onChange={(e) => setFiltroDataInicio(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data Fim
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    value={filtroDataFim}
                    onChange={(e) => setFiltroDataFim(e.target.value)}
                  />
                </div>
              </>
            )}
            
            </div>
        </div>


        {/* TABELA DE DOCUMENTOS – parecido com o layout da imagem */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Nº Documento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Nome Fantasia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    CNPJ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Rubrica
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Data
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {documentos.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      Nenhum documento encontrado para os filtros selecionados.
                    </td>
                  </tr>
                )}

                {documentos.map((doc) => {
                  const statusCfg = getStatusConfig(doc.validacao_status as DocumentoFiscalStatus);
                  return (
                    <tr key={doc.id}>
                      <td className="px-6 py-4 w-32 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {doc.numero_documento || '—'}
                      </td>
                      <td className="px-6 py-4 w-48 text-sm text-gray-900 dark:text-gray-100">
                        {doc.fornecedor_nome || '—'}
                      </td>
                      <td className="px-6 py-4 w-32 text-sm text-gray-700 dark:text-gray-300">
                        {doc.fornecedor_documento || '—'}
                      </td>
                      <td className="px-6 py-4 w-48 text-sm">
                        <button
                          type="button"
                          className="text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200 font-medium"
                        >
                          {doc.rubrica_documento || '—'}
                        </button>
                      </td>
                      <td className="px-6 py-4 w-32 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                        <strong>{formatCurrencyWithSymbol(doc.valor_original)}</strong>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusCfg.className}`}
                        >
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 w-32 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {formatDate((doc as any).data_lancamento || (doc as any).data_emissao)}
                      </td>                      
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
      </main>
    </div>
  );
}
